import { motion, useDragControls, useMotionValue } from 'framer-motion';
import { Maximize2, Minimize2, Minus, X } from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useState,
} from 'react';

import { MAXIMIZE_MARGIN } from '@/components/FloatingWindow/floatingWindow.constants';
import { FloatingWindowContainerContext } from '@/components/FloatingWindow/floatingWindowContainer';
import { Button } from '@/components/ui/button';
import { useWindowResize } from '@/hooks/useWindowResize';
import {
  type FloatingWindowInstance,
  useFloatingWindowsStore,
} from '@/state/floatingWindows.store';

/** Arrow-key move step in px when the title bar is focused. */
const NUDGE = 12;

export interface FloatingWindowFrameProps {
  instance: FloatingWindowInstance;
  /** Derived stacking value (from focusOrder rank). */
  zIndex: number;
  /** Body rendered from the type registry (kept mounted while minimized). */
  children: ReactNode;
}

/**
 * The movable/resizable/minimizable window shell. Non-modal: it never traps
 * focus and never blocks the app behind it. Position is applied on the CSS
 * transform (x/y motion values) and size on width/height, so drag and resize
 * touch different properties and never fight.
 */
export function FloatingWindowFrame({
  instance,
  zIndex,
  children,
}: FloatingWindowFrameProps) {
  const { id, type, title, position, minimized, maximized } = instance;

  const focusWindow = useFloatingWindowsStore(s => s.focusWindow);
  const minimizeWindow = useFloatingWindowsStore(s => s.minimizeWindow);
  const toggleMaximize = useFloatingWindowsStore(s => s.toggleMaximize);
  const closeWindow = useFloatingWindowsStore(s => s.closeWindow);
  const setPosition = useFloatingWindowsStore(s => s.setPosition);
  const setSize = useFloatingWindowsStore(s => s.setSize);

  const dragControls = useDragControls();
  const x = useMotionValue(position.x);
  const y = useMotionValue(position.y);

  // Sync the transform to store-driven position changes (maximize/restore,
  // viewport clamp) — the drag itself writes back through onDragEnd.
  useEffect(() => {
    x.set(position.x);
    y.set(position.y);
  }, [position.x, position.y, x, y]);

  const { size, onResizeHandleMouseDown } = useWindowResize({
    size: instance.size,
    onCommit: next => setSize(id, next),
  });

  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  const startDrag = (e: ReactPointerEvent) => {
    focusWindow(id);
    if (!maximized) dragControls.start(e);
  };

  const handleTitleKeyDown = (e: ReactKeyboardEvent) => {
    if (maximized) return;
    const deltas: Record<string, { dx: number; dy: number }> = {
      ArrowLeft: { dx: -NUDGE, dy: 0 },
      ArrowRight: { dx: NUDGE, dy: 0 },
      ArrowUp: { dx: 0, dy: -NUDGE },
      ArrowDown: { dx: 0, dy: NUDGE },
    };
    const delta = deltas[e.key];
    if (!delta) return;
    e.preventDefault();
    setPosition(id, { x: position.x + delta.dx, y: position.y + delta.dy });
  };

  // ESC minimizes (never closes — don't lose a running job). Bubble phase +
  // guards so an in-body Radix control that consumes ESC wins first.
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    e.stopPropagation();
    minimizeWindow(id);
  };

  return (
    <motion.div
      role="dialog"
      aria-modal={false}
      aria-label={title}
      data-testid={`floating-window-${id}`}
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      onDragEnd={() => setPosition(id, { x: x.get(), y: y.get() })}
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
      onPointerDownCapture={() => focusWindow(id)}
      onKeyDown={handleKeyDown}
      style={
        maximized
          ? {
              // Edge-anchored: CSS stretches it to the viewport in BOTH
              // directions on resize — no width/height, no x/y transform.
              position: 'fixed',
              top: MAXIMIZE_MARGIN,
              left: MAXIMIZE_MARGIN,
              right: MAXIMIZE_MARGIN,
              bottom: MAXIMIZE_MARGIN,
              zIndex,
              display: minimized ? 'none' : 'flex',
            }
          : {
              x,
              y,
              width: size.w,
              height: size.h,
              zIndex,
              position: 'fixed',
              top: 0,
              left: 0,
              display: minimized ? 'none' : 'flex',
            }
      }
      className="border-chariot-stroke bg-chariot-background-primary pointer-events-auto flex-col overflow-hidden rounded-lg border shadow-xl"
    >
      {/* Title bar — the drag handle. */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`${title} window title bar`}
        onPointerDown={startDrag}
        onKeyDown={handleTitleKeyDown}
        className="border-chariot-stroke bg-chariot-background-secondary flex shrink-0 cursor-grab touch-none items-center justify-between gap-2 border-b px-3 py-2 select-none active:cursor-grabbing"
      >
        <span className="text-chariot-text-primary min-w-0 truncate text-sm font-medium">
          {title}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="text-chariot-text-secondary hover:text-chariot-text-primary h-7 w-7"
            aria-label="Minimize"
            onClick={() => minimizeWindow(id)}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-chariot-text-secondary hover:text-chariot-text-primary h-7 w-7"
            aria-label={maximized ? 'Restore' : 'Maximize'}
            onClick={() => toggleMaximize(id)}
          >
            {maximized ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-chariot-text-secondary hover:text-chariot-stroke-critical h-7 w-7"
            aria-label="Close"
            onClick={() => closeWindow(id)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Body — content-agnostic; the registered component renders here and
          keeps its own live data subscriptions. */}
      <FloatingWindowContainerContext.Provider value={containerEl}>
        <div
          ref={setContainerEl}
          data-window-type={type}
          className="text-chariot-text-primary relative min-h-0 flex-1 overflow-auto"
        >
          {children}
        </div>
      </FloatingWindowContainerContext.Provider>

      {/* Bottom-right resize handle. */}
      {!maximized && (
        <div
          role="presentation"
          aria-hidden="true"
          onMouseDown={onResizeHandleMouseDown}
          className="absolute right-0 bottom-0 z-10 h-4 w-4 cursor-nwse-resize"
          style={{
            background:
              'linear-gradient(135deg, transparent 0 50%, var(--color-chariot-stroke, currentColor) 50% 60%, transparent 60% 70%, var(--color-chariot-stroke, currentColor) 70% 80%, transparent 80%)',
          }}
        />
      )}
    </motion.div>
  );
}
